import React, { Fragment, useState } from 'react';
import { get } from 'lodash/fp';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { Button, Icon, Table } from 'rsuite';
import styled from 'styled-components';
import { UserType } from '../../../../../interfaces/portal';
import isPortalGuestPhoneSynced from '../../../../../helpers/isPortalGuestPhoneSynced';
import ConfirmationModal from '../../../../ConfirmationModal';
import { syncPortalUserGuestPhone } from '../../../../../redux/Settings/sagas/portal/routines';

const PhoneLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #333;
  line-height: 1.2;
`;

const PhoneValue = styled.div`
  margin-bottom: 6px;
  line-height: 1.3;
`;

const SyncButton = styled(Button)`
  padding: 0;
  height: auto;
  font-size: 12px;
`;

const formatPhoneLine = (homePhone, workPhone, cellPhone) => {
  const parts = [];
  const home = (homePhone || '').trim();
  const work = (workPhone || '').trim();
  const cell = (cellPhone || '').trim();
  if (home) {
    parts.push(`Home: ${home}`);
  }
  if (work) {
    parts.push(`Work: ${work}`);
  }
  if (cell) {
    parts.push(`Cell: ${cell}`);
  }
  return parts.length ? parts.join(', ') : '—';
};

const PhoneCell = ({ rowData, ...props }) => {
  const dispatch = useDispatch();
  const [showConfirm, setShowConfirm] = useState(false);
  const otherInfo = get('otherInfo', rowData) || {};
  const linkedGuest = get('linkedGuest', rowData);
  const portalPhone = formatPhoneLine(
    get('homePhone', otherInfo),
    get('workPhone', otherInfo),
    get('cellPhone', otherInfo)
  );
  const guestPhone = formatPhoneLine(
    get('homePhone', linkedGuest),
    get('workPhone', linkedGuest),
    get('cellPhone', linkedGuest)
  );
  const isPhoneSynced = isPortalGuestPhoneSynced(otherInfo, linkedGuest);
  const showSyncDetails = linkedGuest && !isPhoneSynced;

  const handleConfirm = () => {
    if (!rowData || !rowData.id) return;
    setShowConfirm(false);
    dispatch(syncPortalUserGuestPhone(rowData.id));
  };

  return (
    <Table.Cell style={{ marginTop: -8 }} {...props}>
      {showSyncDetails ? (
        <Fragment>
          <PhoneLabel>Portal Phone</PhoneLabel>
          <PhoneValue>{portalPhone}</PhoneValue>
          <PhoneLabel>Internal Guest Phone</PhoneLabel>
          <PhoneValue style={{ marginBottom: 4 }}>{guestPhone}</PhoneValue>
          <SyncButton
            appearance="link"
            onClick={e => {
              e.stopPropagation();
              setShowConfirm(true);
            }}
          >
            <Icon icon="refresh" /> Sync Phone
          </SyncButton>
          <ConfirmationModal
            show={showConfirm}
            title="Sync Phone"
            cancelButtonLabel="Cancel"
            confirmButtonLabel="Sync Phone"
            onCancel={() => setShowConfirm(false)}
            onConfirm={handleConfirm}
          >
            This will overwrite the internal guest phone numbers with the Portal
            phone numbers. Do you want to continue?
          </ConfirmationModal>
        </Fragment>
      ) : (
        portalPhone
      )}
    </Table.Cell>
  );
};

PhoneCell.propTypes = {
  dataKey: PropTypes.string.isRequired,
  rowData: UserType
};

PhoneCell.defaultProps = {
  rowData: null
};

export default PhoneCell;
